import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';

const { FiClock, FiTrendingUp, FiCoffee, FiDollarSign, FiUsers, FiZap } = FiIcons;

function IdleManager() {
  const { state, dispatch } = useGame();
  const [idleEarnings, setIdleEarnings] = useState(0);
  const [idleCustomers, setIdleCustomers] = useState(0);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const [showIdleReport, setShowIdleReport] = useState(false);

  // Calculate idle production rates based on upgrades AND auto baristas
  const getIdleRates = () => {
    // Base rate from cafe upgrades
    const baseRate = 0.5; // Base coins per second
    const machineMultiplier = (state.cafeUpgrades.espressoMachine || 1) * 0.3;
    const decorationMultiplier = (state.cafeUpgrades.decoration || 1) * 0.2;
    const levelMultiplier = state.level * 0.1;
    
    // NEW: Auto Barista Production
    let baristaProduction = 0;
    let totalBaristas = 0;
    
    if (state.autoBaristas) {
      Object.entries(state.autoBaristas).forEach(([baristaId, barista]) => {
        if (barista.count > 0) {
          // Each barista contributes based on their efficiency
          baristaProduction += barista.count * barista.efficiency * 1.2; // 1.2 coins per second per barista
          totalBaristas += barista.count;
        }
      });
    }

    const coinsPerSecond = baseRate + machineMultiplier + decorationMultiplier + levelMultiplier + baristaProduction;
    const customersPerMinute = Math.max(1, Math.floor(coinsPerSecond * 0.8) + totalBaristas);

    return {
      coinsPerSecond,
      customersPerMinute,
      experiencePerMinute: Math.floor(coinsPerSecond * 2),
      baristaContribution: baristaProduction,
      totalBaristas
    };
  };

  // Calculate offline earnings when returning to game
  const calculateOfflineEarnings = (offlineTime) => {
    const rates = getIdleRates();
    const hours = Math.min(offlineTime / (1000 * 60 * 60), 8); // Max 8 hours
    
    const earnings = Math.floor(rates.coinsPerSecond * hours * 3600);
    const customers = Math.floor(rates.customersPerMinute * hours * 60);
    const experience = Math.floor(rates.experiencePerMinute * hours * 60);
    
    return {
      earnings,
      customers,
      experience,
      hours,
      baristaContribution: rates.baristaContribution,
      totalBaristas: rates.totalBaristas
    };
  };

  // Check for offline progress on component mount
  useEffect(() => {
    const savedTime = localStorage.getItem('lastActiveTime');
    if (savedTime) {
      const offlineTime = Date.now() - parseInt(savedTime);
      if (offlineTime > 60000) { // If offline for more than 1 minute
        const progress = calculateOfflineEarnings(offlineTime);
        setIdleEarnings(progress.earnings);
        setIdleCustomers(progress.customers);
        setShowIdleReport(true);

        // Apply the earnings
        dispatch({ type: 'ADD_COINS', payload: progress.earnings });
        dispatch({ type: 'ADD_EXPERIENCE', payload: progress.experience });
        dispatch({ 
          type: 'UPDATE_STATS', 
          payload: {
            drinksServed: (state.stats.drinksServed || 0) + progress.customers,
            totalEarnings: (state.stats.totalEarnings || 0) + progress.earnings
          }
        });
      }
    }
    setLastActiveTime(Date.now());
  }, []);

  // Save active time when component unmounts or page unloads
  useEffect(() => {
    const saveActiveTime = () => {
      localStorage.setItem('lastActiveTime', Date.now().toString());
    };

    window.addEventListener('beforeunload', saveActiveTime);
    window.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        saveActiveTime();
      }
    });

    return () => {
      window.removeEventListener('beforeunload', saveActiveTime);
      window.removeEventListener('visibilitychange', saveActiveTime);
      saveActiveTime();
    };
  }, []);

  const handleClaimOfflineEarnings = () => {
    setShowIdleReport(false);
    setIdleEarnings(0);
    setIdleCustomers(0);
  };

  const rates = getIdleRates();

  return (
    <>
      {/* Enhanced Idle Progress Indicator - Shows current rates including baristas */}
      <div className="fixed top-20 right-4 z-30 bg-white/90 backdrop-blur-sm rounded-xl p-3 shadow-lg border-2 border-purple-200">
        <div className="text-center">
          <div className="flex items-center mb-2">
            <SafeIcon icon={FiZap} className="text-purple-500 mr-2" />
            <span className="text-sm font-bold text-purple-700">Auto Production</span>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <SafeIcon icon={FiDollarSign} className="text-green-500" />
              <span className="text-green-600 font-medium">
                +${rates.coinsPerSecond.toFixed(1)}/s
              </span>
            </div>
            <div className="flex items-center justify-between">
              <SafeIcon icon={FiUsers} className="text-blue-500" />
              <span className="text-blue-600 font-medium">
                {rates.customersPerMinute}/min
              </span>
            </div>
            <div className="flex items-center justify-between">
              <SafeIcon icon={FiTrendingUp} className="text-purple-500" />
              <span className="text-purple-600 font-medium">
                +{rates.experiencePerMinute} XP/min
              </span>
            </div>
            {rates.totalBaristas > 0 && (
              <div className="pt-1 mt-1 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <SafeIcon icon={FiCoffee} className="text-amber-500" />
                  <span className="text-amber-600 font-medium">
                    {rates.totalBaristas} Baristas
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Offline Earnings Report */}
      <AnimatePresence>
        {showIdleReport && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-6 shadow-2xl text-center max-w-sm w-full"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
            >
              <div className="text-6xl mb-4">💰</div>
              <h2 className="text-2xl font-bold text-purple-600 mb-4">
                Welcome Back!
              </h2>
              <p className="text-gray-600 mb-6">
                Your {rates.totalBaristas > 0 ? 'barista team' : 'cafe'} kept running while you were away
              </p>

              {/* Enhanced Earnings Summary */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      ${idleEarnings}
                    </div>
                    <div className="text-sm text-gray-500">Coins Earned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {idleCustomers}
                    </div>
                    <div className="text-sm text-gray-500">Customers Served</div>
                  </div>
                </div>
                
                {/* Barista Contribution */}
                {rates.totalBaristas > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-center text-purple-600 mb-2">
                      <SafeIcon icon={FiUsers} className="mr-2" />
                      <span className="font-bold">Your {rates.totalBaristas} Baristas</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Contributed ${Math.floor(rates.baristaContribution * (idleEarnings / rates.coinsPerSecond))} 
                      ({Math.round((rates.baristaContribution / rates.coinsPerSecond) * 100)}% of earnings)
                    </div>
                  </div>
                )}

                <div className="mt-3 text-center">
                  <div className="text-lg font-bold text-purple-600">
                    +{Math.floor(idleCustomers * 2)} XP
                  </div>
                  <div className="text-sm text-gray-500">Experience Gained</div>
                </div>
              </div>

              <motion.button
                className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-3 rounded-2xl font-bold w-full"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleClaimOfflineEarnings}
              >
                Claim Rewards!
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default IdleManager;